import {
    Button,
    Checkbox,
    FormControl,
    Input,
    Link,
    Message,
    Spacer,
    Stack,
    Text,
    useCheckboxState,
} from '@kolonialno/lama';
import { useRef, useState } from 'react';
import { Link as ReactRouterLink, useSearchParams } from 'react-router';

import { useCSRFToken } from '../../../utils/csrf';
import { useFormErrors } from '../../../utils/viewArgs';
import Container from '../../components/Container';
import { useButtonLoading } from '../../utils/button-state';
import { useAuthenticationContext } from '../../utils/context';
import { withRedirect } from '../../utils/redirect';

export function SetPassword() {
    const { disabled, loadingState, setLoading } = useButtonLoading();
    const [searchParams] = useSearchParams();
    const csrf = useCSRFToken();
    const context = useAuthenticationContext();
    const formErrors = useFormErrors() as Record<string, string[]>;

    const hasErrors = Object.keys(formErrors).length > 0;
    const linkErrors = formErrors.__all__ ?? [];
    const passwordErrors = formErrors.new_password ?? [];

    let errorMessage = null;
    if (linkErrors.length > 0) {
        errorMessage = (
            <Message variant="danger">
                {linkErrors.join(' ')}{' '}
                <Link
                    as={ReactRouterLink}
                    to={withRedirect(
                        '/authentication/forgot-password/',
                        context.redirect,
                    )}
                >
                    Request a new link
                </Link>
            </Message>
        );
    } else if (passwordErrors.length > 0) {
        errorMessage = (
            <Message variant="danger">{passwordErrors.join(' ')}</Message>
        );
    } else if (hasErrors) {
        errorMessage = (
            <Message variant="danger">Could not create new password</Message>
        );
    }

    const [password, setPassword] = useState<string>('');
    const passwordRef = useRef<HTMLInputElement>(null);
    const checkboxState = useCheckboxState();

    const id = searchParams.get('id');
    const token = searchParams.get('token');
    const newUser = searchParams.get('new-user');

    return (
        <Container>
            {errorMessage}
            <Stack>
                <Text
                    variant="subtitle1"
                    className="white-text"
                    alignment="center"
                >
                    Create new password
                </Text>
                <Text className="white-text" alignment="center">
                    Use at least 8 characters. Avoid common passwords, passwords
                    made only of numbers, and anything close to your name or
                    email address.
                </Text>

                <form
                    method="post"
                    onSubmit={() => {
                        setLoading();
                    }}
                >
                    <input
                        type="hidden"
                        name="csrfmiddlewaretoken"
                        value={csrf}
                    />
                    <input type="hidden" name="id" value={id || undefined} />
                    <input
                        type="hidden"
                        name="token"
                        value={token || undefined}
                    />
                    <input
                        type="hidden"
                        name="new_user"
                        value={newUser || undefined}
                    />
                    <input
                        type="hidden"
                        name="rd"
                        value={context.redirect || undefined}
                    />

                    <Stack spacing="s">
                        <Stack direction="horizontal" alignItems="flex-start">
                            <FormControl
                                name="new_password"
                                label="New password"
                                className="form-control"
                            >
                                <Input
                                    ref={passwordRef}
                                    onChange={({ target }) =>
                                        setPassword(target.value)
                                    }
                                    value={password}
                                    type={
                                        checkboxState.checked
                                            ? 'text'
                                            : 'password'
                                    }
                                    required
                                    autoCapitalize="none"
                                    autoComplete="new-password"
                                />
                            </FormControl>
                        </Stack>

                        <Checkbox {...checkboxState} className="white-text">
                            Show password
                        </Checkbox>

                        <Spacer spacing="xs" />

                        <Button
                            type="submit"
                            variant="primary"
                            size="m"
                            disabled={disabled}
                            loadingState={loadingState}
                        >
                            Create password
                        </Button>
                    </Stack>
                </form>
            </Stack>
        </Container>
    );
}
